<template>
    <div id="org_settings_notification">
        <div class="">
            <div class="d-flex align-items-center justify-content-between">
                <h3 class="mb-0">Email Notification</h3>
                <!-- Auto-save status indicator -->
                <div v-show="filteredNotifications.length !== 0">
                    <small v-if="form.busy" class="text-muted">
                        <i class="fa fa-spinner fa-spin me-1"></i> Saving...
                    </small>
                    <small v-else-if="form.lastSaved" class="text-success">
                        <i class="fa fa-check me-1"></i> Saved
                    </small>
                </div>
            </div>
            
            
            <div v-show="filteredNotifications.length === 0 && user_notification.length > 0">
                <p class="text-muted">No notification options available based on your permissions.</p>
            </div>
            <div v-show="user_notification.length === 0">
                <h4 class="d-flex align-items-center">
                    <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    Loading Notification Options
                </h4>
            </div>
            
            <div v-for="n in filteredNotifications" :key="n.name" class="d-flex justify-content-between align-items-center py-2">
                <div class="flex-grow-1">
                    <span class="text-dark">{{n.name}}</span>
                </div>
                <div class="form-check form-switch">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        role="switch" 
                        v-model="n.active"
                        @change="onToggleChange"
                     aria-label="Active">
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';
import { useNotifications } from '../../../composables/useNotifications';
import permissionsMixin from '../../../mixins/permissions';

export default defineComponent({

    mixins: [permissionsMixin],
    name: 'NotificationSettings',
    components: {
        Swal
    },
    setup() {
        const { showNotification } = useNotifications();
        return {
            showNotification
        };
    },
    props: {
        client: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            local_client: {},
            form: {
                busy: false,
                error: false,
                lastSaved: false
            },
            user_notification: [],
            autoSaveTimeout: null
        };
    },
    computed: {
        filteredNotifications() {
            return this.user_notification.filter(notification => {
                return this.hasPermissionForNotification(notification.name);
            });
        }
    },
    beforeMount() {
        this.local_client = this.client;
        this.getNotificationOptions();
    },
    methods: {
        hasPermissionForNotification(notificationName) {
            // Map notification categories to permission sections
            const mapping = {
                'Insights': 'insights',
                'Integrations': 'connections',
                'Files': 'connections',
                'Billing': 'billing'
            };
            
            const section = mapping[notificationName];
            
            // If no mapping exists, show the notification (for backward compatibility)
            if (!section) {
                return true;
            }
            
            // Check if user has view permission for the section
            return this.canView(section);
        },
        getNotificationOptions() {
            window.axios.get('/api/notification/'+this.client.url+'/options', [])
              .then(response => {
                    const prefs = response.data.notifications; // {Integrations:true,...}
                    this.user_notification = Object.keys(prefs)
                        .map(k=>({name:k,active:prefs[k]}))
                        .sort((a, b) => a.name.localeCompare(b.name));
              });
        },
        onToggleChange() {
            // Trigger auto-save when toggle changes
            this.debouncedAutoSave();
        },
        debouncedAutoSave() {
            // Clear existing timeout
            if (this.autoSaveTimeout) {
                clearTimeout(this.autoSaveTimeout);
            }
            
            // Set new timeout for auto-save (1 second delay)
            this.autoSaveTimeout = setTimeout(() => {
                this.autoSave();
            }, 1000);
        },
        autoSave() {
            if (this.form.busy || this.filteredNotifications.length === 0) {
                return;
            }
            
            this.form.busy = true;

            // Only save preferences for notifications the user has permission to see
            const obj = {};
            this.filteredNotifications.forEach(n=>{obj[n.name]=n.active});
            this.local_client.notification_preference = obj;

            window.axios.put('/api/client/' + this.local_client.url, this.local_client)
            .then(response => {
                this.form.busy = false;
                this.form.lastSaved = true;
                
                this.showNotification({
                    text: 'Your notification settings were saved!'
                });
                
                // Hide the "Saved" indicator after 3 seconds
                setTimeout(() => {
                    this.form.lastSaved = false;
                }, 3000);
            }).catch(error => {
                this.form.busy = false;
                this.form.error = true;
                setTimeout(() => {
                    this.form.error = false;
                }, 10000);
            });
        },
        update() {
            // Manual save method (kept for compatibility)
            this.autoSave();
        },
    }
});
</script>
